import QueryString from 'query-string';
import React, { useContext, useRef, useEffect, useState } from 'react';

import { ArrowRightIcon, CloseIcon } from '$partials/icon';
import { ModalContext } from '$contexts/modal.jsx';
import { useCursorContext } from '$contexts/cursor.jsx';
import { Button, Select } from '$partials/ui';
import { formatNumber, formatPrice } from '$utilities/format';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').SearchModal}
 */
const SearchModal = ({propertyListing, rentalPropertyListing}) => {
    const { handleModal } = useContext(ModalContext);
    const { dispatch: cursorDispatch } = useCursorContext();
    const exitButtonRef = useRef(null);

    const [ activeFilter, setActiveFilter ] = useState('property');
    const [ priceFilters, setPriceFilters ] = useState(propertyListing.priceFilterOptions);
    const [ bedroomFilters, setBedroomFilters ] = useState(propertyListing.bedroomFilterOptions);
    const [ maxPrice, setMaxPrice ] = useState('');
    const [ minPrice, setMinPrice ] = useState('');
    const [ minBedroom, setMinBedroom ] = useState('');

    useEffect(() => {
        const exit = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: exitButtonRef,
                cursorType: 'exit'
            },
        });

        return () => [exit].map(cleanup => {
            cleanup();});
    }, []);

    const queryString = () => {
        let queryParameters = { page: 1 };
        const query = {
            'maxPrice': maxPrice,
            'minPrice': minPrice,
            'minBedroom': minBedroom,
        };
        if (query) {
            Object.keys(query).forEach((k) => {
                if (Array.isArray(query[k]) ? query[k].length > 0 && query[k][0] !== null : query[k] !== null) {
                    queryParameters[k] = query[k];
                }
            });
        }
        return `?${QueryString.stringify(queryParameters)}`;
    };

    return (
        <>
            <div
                className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-black mix-blend-multiply z3 cursor-none'}
                onClick={() => handleModal()}
                ref={exitButtonRef}
                style={{opacity: '0.2'}}>
            </div>
            <div className='bg-white z4 fixed top-0 overflow-auto width-100 search-modal-container'>
                <div className='container'>
                    <button
                        aria-label='Close'
                        className='form-field-reset link-reset absolute right-0 right mt6 mr6 cursor-pointer'
                        onClick={() => handleModal()}
                        type='button'>
                        <CloseIcon/>
                    </button>
                    <div className='pt7 pb7 md-pb9'>
                        <h2 className='type-style-2'>Find Properties</h2>
                    </div>
                    <div className='col-12 flex mb5 md-mb6'>
                        <div
                            className={`col-6 sm-col-4 md-col-2 py4 md-py5 pl5 md-pl6 border cursor-pointer${activeFilter === 'property' ? ' bg-yellow' : ''}`}
                            onClick={()=> {
                                setPriceFilters(propertyListing.priceFilterOptions);
                                setBedroomFilters(propertyListing.bedroomFilterOptions);
                                setActiveFilter('property');
                            }}>
                            <p className='type-style-5'>For Sale</p>
                        </div>
                        <div
                            className={`col-6 sm-col-4 md-col-2 py4 md-py5 pl5 md-pl6 border-top border-right border-bottom cursor-pointer${activeFilter === 'rental' ? ' bg-yellow' : ''}`}
                            onClick={()=>{
                                setPriceFilters(rentalPropertyListing.pricePerWeekFilterOptions);
                                setBedroomFilters(rentalPropertyListing.bedroomFilterOptions);
                                setActiveFilter('rental');
                            }}>
                            <p className='type-style-5'>For Rent</p>
                        </div>
                    </div>
                    <div className='col-12 flex flex-wrap'>
                        <div className='col-12 sm-col-4 md-col-4 relative border black'>
                            <Select
                                className='modal-search--select'
                                label='Min Price'
                                onChange={(value) => {
                                    setMinPrice(value);
                                }}
                                options={[{label:'No min', value:''}].concat(priceFilters?.map(option=> {return {label: formatPrice(option), value: option};}))}
                                value={minPrice}/>
                        </div>
                        <div className='col-12 sm-col-4 md-col-4 relative border-left border-right border-bottom sm-border-0 sm-border-right sm-border-top sm-border-bottom black'>
                            <Select
                                className='modal-search--select'
                                label='Max Price'
                                onChange={(value) => {
                                    setMaxPrice(value);
                                }}
                                options={priceFilters?.map(option=> {return {label: formatPrice(option), value: option};}).concat([{label:'No max', value:''}])}
                                value={maxPrice} />
                        </div>
                        <div className='col-12 sm-col-4 md-col-4 relative border-bottom border-left border-right sm-border-0 sm-border-top sm-border-right sm-border-bottom md-border-0 md-border-right md-border-top md-border-bottom black'>
                            <Select
                                className='modal-search--select'
                                label='Min Bedrooms'
                                onChange={(value) => {
                                    setMinBedroom(value);
                                }}
                                options={[{label:'No min', value:''}].concat(bedroomFilters?.map(option=> {return {label: formatNumber(option), value: option};}))}
                                value={minBedroom} />
                        </div>
                    </div>
                    <div className='col-12 flex justify-end mb7 md-mb11 mt6 sm-mt0'>
                        <div className='col-12 sm-col-4 white'>
                            <Button
                                as = 'a'
                                className = 'bg-black white type-style-5'
                                href={`${activeFilter === 'rental' ? rentalPropertyListing.url : propertyListing.url}/${queryString()}`}
                                look = 'fill'
                                onClick={() => {
                                    handleModal();
                                }}
                                renderIcon = {<ArrowRightIcon/>}>
                                View Results
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default SearchModal ;
